import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Robust Lazy Loader with automatic retry mechanism
 * Prevents "Failed to fetch dynamically imported module" errors during server restarts or transient network drops.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T } | { [key: string]: any }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      if ('default' in module && module.default) {
        return { default: module.default };
      }
      const firstExport = Object.values(module).find((v) => typeof v === 'function');
      if (firstExport) {
        return { default: firstExport as T };
      }
      return { default: (module as any).default || (module as any) };
    } catch (error) {
      console.warn('Dynamic import failed, retrying module load in 600ms...', error);
      try {
        await new Promise((resolve) => setTimeout(resolve, 600));
        const module = await factory();
        if ('default' in module && module.default) {
          return { default: module.default };
        }
        const firstExport = Object.values(module).find((v) => typeof v === 'function');
        if (firstExport) {
          return { default: firstExport as T };
        }
        return { default: (module as any).default || (module as any) };
      } catch (retryErr) {
        console.error('Dynamic module import failed after retry:', retryErr);
        throw retryErr;
      }
    }
  });
}
